/**
 * Content that rises into place the first time it scrolls into view, as on the public pages.
 * People who turned motion off see it at once (styles.css), as do browsers that cannot watch the
 * page's scrolling.
 */

import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

export function Reveal({
  delay = 0,
  className = "",
  children,
}: {
  /** Milliseconds to wait once in view, to stagger items that appear together. */
  delay?: number;
  className?: string;
  children: ReactNode;
}) {
  const element = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"waiting" | "shown">(() =>
    typeof IntersectionObserver === "undefined" ? "shown" : "waiting",
  );

  useEffect(() => {
    const target = element.current;
    if (!target || state === "shown") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setState("shown");
          observer.disconnect();
        }
      },
      // Once a little of it is above the bottom of the screen.
      { rootMargin: "0px 0px -6% 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [state]);

  return (
    <div
      ref={element}
      data-reveal={state}
      style={delay ? ({ "--reveal-delay": `${delay}ms` } as CSSProperties) : undefined}
      className={className}
    >
      {children}
    </div>
  );
}
